import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { PERMISSIONS } from '@pospak/shared';
import { AppLayout } from './AppLayout';
import { RequireAuth, RequireModule, RequirePermission } from './guards';

const Login = lazy(() => import('../features/login'));
const Dashboard = lazy(() => import('../features/dashboard'));
const Catalog = lazy(() => import('../features/catalog'));
const CategoryTree = lazy(() => import('../features/catalog/CategoryTree'));
const ProductEditor = lazy(() => import('../features/catalog/ProductEditor'));
const ImportWizard = lazy(() => import('../features/catalog/ImportWizard'));
const Pricing = lazy(() => import('../features/pricing'));
const Inventory = lazy(() => import('../features/inventory'));
const Shifts = lazy(() => import('../features/shifts'));
const Purchasing = lazy(() => import('../features/purchasing'));
const Customers = lazy(() => import('../features/customers'));
const CustomerDetail = lazy(() => import('../features/customers/CustomerDetail'));
const Vouchers = lazy(() => import('../features/vouchers'));
const Accounts = lazy(() => import('../features/accounts'));
const Reports = lazy(() => import('../features/reports'));
const Orders = lazy(() => import('../features/orders'));
const Mobile = lazy(() => import('../features/mobile'));
const Quotations = lazy(() => import('../features/quotations'));
const BulkOrder = lazy(() => import('../features/bulk-order'));
const Hr = lazy(() => import('../features/hr'));
const Fbr = lazy(() => import('../features/fbr'));
const Sync = lazy(() => import('../features/sync'));
const Selling = lazy(() => import('../features/selling'));
const Bills = lazy(() => import('../features/selling/Bills'));
const BillDetail = lazy(() => import('../features/selling/BillDetail'));
const OfflineSync = lazy(() => import('../features/offline-sync'));
const Delivery = lazy(() => import('../features/delivery'));
const DeliveryRunDetail = lazy(() => import('../features/delivery/RunDetail'));
const ReceiptTemplates = lazy(() => import('../features/receipt-templates'));
const Shops = lazy(() => import('../features/shops'));
const Users = lazy(() => import('../features/users'));
const Roles = lazy(() => import('../features/roles'));
const Settings = lazy(() => import('../features/settings'));
const OwnerPortal = lazy(() => import('../features/owner'));
const ClientPortal = lazy(() => import('../features/client'));
const Support = lazy(() => import('../features/support'));
const Portal = lazy(() => import('../features/portal'));
const ChangePassword = lazy(() => import('../features/change-password'));
const StorefrontShopList = lazy(() => import('../features/storefront/ShopList'));
const StorefrontShopHome = lazy(() => import('../features/storefront/ShopHome'));
const StorefrontCart = lazy(() => import('../features/storefront/Cart'));
const StorefrontCheckout = lazy(() => import('../features/storefront/Checkout'));
const StorefrontTracking = lazy(() => import('../features/storefront/Tracking'));
const HomePage = lazy(() => import('./home/HomePage'));

function Fallback() {
  return (
    <div className="p-8 text-sm text-ink3" role="status" aria-live="polite">
      Loading…
    </div>
  );
}

function Lazy() {
  return (
    <Suspense fallback={<Fallback />}>
      <Outlet />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  // Public marketing landing page — no auth, no app chrome (app/home/). Staff
  // reach the console from its "Sign in" CTA; the console itself lives at
  // /dashboard, /sell, … below. A signed-in visitor sees the page with an
  // "Enter console" CTA — never an automatic redirect.
  { path: '/', element: <Suspense fallback={<Fallback />}><HomePage /></Suspense> },
  { path: '/login', element: <Suspense fallback={<Fallback />}><Login /></Suspense> },
  { path: '/owner/*', element: <Suspense fallback={<Fallback />}><OwnerPortal /></Suspense> },
  { path: '/portal', element: <Suspense fallback={<Fallback />}><Portal /></Suspense> },
  { path: '/client/*', element: <Suspense fallback={<Fallback />}><ClientPortal /></Suspense> },
  { path: '/s', element: <Suspense fallback={<Fallback />}><StorefrontShopList /></Suspense> },
  { path: '/s/:slug', element: <Suspense fallback={<Fallback />}><StorefrontShopHome /></Suspense> },
  { path: '/s/:slug/cart', element: <Suspense fallback={<Fallback />}><StorefrontCart /></Suspense> },
  { path: '/s/:slug/checkout', element: <Suspense fallback={<Fallback />}><StorefrontCheckout /></Suspense> },
  { path: '/s/:slug/order/:token', element: <Suspense fallback={<Fallback />}><StorefrontTracking /></Suspense> },
  {
    path: '/change-password',
    element: (
      <RequireAuth allowMustChangePassword>
        <Suspense fallback={<Fallback />}>
          <ChangePassword />
        </Suspense>
      </RequireAuth>
    ),
  },
  {
    // Authenticated app shell — a PATHLESS layout route at the root, so the
    // feature routes still resolve at /sell, /bills, … while `/` itself stays
    // the public landing page above. Login lands on /dashboard.
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      {
        element: <Lazy />,
        children: [
          { path: 'dashboard', element: <Dashboard /> },
          {
            path: 'sell',
            element: (
              <RequirePermission permission={PERMISSIONS.SALES_CREATE}>
                <Selling />
              </RequirePermission>
            ),
          },
          {
            path: 'bills',
            element: (
              <RequirePermission permission={PERMISSIONS.SALES_VIEW}>
                <Bills />
              </RequirePermission>
            ),
          },
          {
            path: 'bills/:id',
            element: (
              <RequirePermission permission={PERMISSIONS.SALES_VIEW}>
                <BillDetail />
              </RequirePermission>
            ),
          },
          {
            path: 'offline-sync',
            element: (
              <RequirePermission permission={PERMISSIONS.SALES_VIEW}>
                <OfflineSync />
              </RequirePermission>
            ),
          },
          {
            path: 'catalog',
            element: (
              <RequirePermission permission={PERMISSIONS.CATALOG_VIEW}>
                <Catalog />
              </RequirePermission>
            ),
          },
          {
            path: 'catalog/import',
            element: (
              <RequirePermission permission={PERMISSIONS.CATALOG_IMPORT}>
                <ImportWizard />
              </RequirePermission>
            ),
          },
          {
            path: 'catalog/categories',
            element: (
              <RequirePermission permission={PERMISSIONS.CATALOG_VIEW}>
                <CategoryTree />
              </RequirePermission>
            ),
          },
          {
            // `new` and `:id` share one editor — creating and editing a product
            // are the same screen, so they can never drift apart.
            path: 'catalog/:id',
            element: (
              <RequirePermission permission={PERMISSIONS.CATALOG_VIEW}>
                <ProductEditor />
              </RequirePermission>
            ),
          },
          {
            path: 'pricing',
            element: (
              <RequirePermission permission={PERMISSIONS.PRICING_VIEW}>
                <Pricing />
              </RequirePermission>
            ),
          },
          {
            path: 'inventory',
            element: (
              <RequirePermission permission={PERMISSIONS.INVENTORY_VIEW}>
                <Inventory />
              </RequirePermission>
            ),
          },
          {
            path: 'shift',
            element: (
              <RequirePermission permission={PERMISSIONS.SHIFT_VIEW}>
                <Shifts />
              </RequirePermission>
            ),
          },
          {
            path: 'purchasing',
            element: (
              <RequirePermission permission={PERMISSIONS.PURCHASE_VIEW}>
                <Purchasing />
              </RequirePermission>
            ),
          },
          {
            path: 'customers',
            element: (
              <RequirePermission permission={PERMISSIONS.CUSTOMERS_VIEW}>
                <Customers />
              </RequirePermission>
            ),
          },
          {
            path: 'customers/:id',
            element: (
              <RequirePermission permission={PERMISSIONS.CUSTOMERS_VIEW}>
                <CustomerDetail />
              </RequirePermission>
            ),
          },
          {
            path: 'accounts',
            element: (
              <RequirePermission permission={PERMISSIONS.ACCOUNTS_VIEW}>
                <Accounts />
              </RequirePermission>
            ),
          },
          {
            path: 'orders',
            element: (
              <RequirePermission permission={PERMISSIONS.ORDERS_VIEW}>
                <Orders />
              </RequirePermission>
            ),
          },
          {
            path: 'delivery',
            element: (
              <RequirePermission permission={PERMISSIONS.DELIVERY_VIEW}>
                <Delivery />
              </RequirePermission>
            ),
          },
          {
            path: 'delivery/:id',
            element: (
              <RequirePermission permission={PERMISSIONS.DELIVERY_VIEW}>
                <DeliveryRunDetail />
              </RequirePermission>
            ),
          },
          {
            path: 'mobile',
            element: (
              <RequireModule module="mobile">
                <RequirePermission permission={PERMISSIONS.MOBILE_VIEW}>
                  <Mobile />
                </RequirePermission>
              </RequireModule>
            ),
          },
          {
            path: 'quotations',
            element: (
              <RequirePermission permission={PERMISSIONS.QUOTATIONS_VIEW}>
                <Quotations />
              </RequirePermission>
            ),
          },
          {
            path: 'bulk-order',
            element: (
              <RequirePermission permission={PERMISSIONS.SALES_CREATE}>
                <BulkOrder />
              </RequirePermission>
            ),
          },
          {
            path: 'vouchers',
            element: (
              <RequirePermission permission={PERMISSIONS.SALES_VOUCHER_MANAGE}>
                <Vouchers />
              </RequirePermission>
            ),
          },
          {
            path: 'hr',
            element: (
              <RequirePermission permission={PERMISSIONS.HR_VIEW}>
                <Hr />
              </RequirePermission>
            ),
          },
          {
            path: 'fbr',
            element: (
              <RequirePermission permission={PERMISSIONS.FBR_VIEW}>
                <Fbr />
              </RequirePermission>
            ),
          },
          {
            path: 'sync',
            element: (
              <RequirePermission permission={PERMISSIONS.SYNC_VIEW}>
                <Sync />
              </RequirePermission>
            ),
          },
          {
            path: 'reports',
            element: (
              <RequirePermission permission={PERMISSIONS.REPORTS_SALES_VIEW}>
                <Reports />
              </RequirePermission>
            ),
          },
          {
            path: 'shops',
            element: (
              <RequirePermission permission={PERMISSIONS.SHOPS_VIEW}>
                <Shops />
              </RequirePermission>
            ),
          },
          {
            path: 'users',
            element: (
              <RequirePermission permission={PERMISSIONS.USERS_VIEW}>
                <Users />
              </RequirePermission>
            ),
          },
          {
            path: 'roles',
            element: (
              <RequirePermission permission={PERMISSIONS.ROLES_VIEW}>
                <Roles />
              </RequirePermission>
            ),
          },
          {
            path: 'support',
            element: (
              <RequirePermission permission={PERMISSIONS.SUPPORT_VIEW}>
                <Support />
              </RequirePermission>
            ),
          },
          {
            path: 'settings',
            element: (
              <RequirePermission permission={PERMISSIONS.SETTINGS_VIEW}>
                <Settings />
              </RequirePermission>
            ),
          },
          {
            path: 'receipt-templates',
            element: (
              <RequirePermission permission={PERMISSIONS.SETTINGS_VIEW}>
                <ReceiptTemplates />
              </RequirePermission>
            ),
          },
          { path: '*', element: <Navigate to="/dashboard" replace /> },
        ],
      },
    ],
  },
]);
